import React, { useRef, useState } from 'react';
import {
  useGetList,
  useCreate,
  useUpdate,
  useDelete,
  useNotify,
} from 'react-admin';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import {
  LocationAutocompleteInput,
  SelectedPlace,
} from './LocationAutocompleteInput';

interface CompanyLocation {
  id: number;
  label: string;
  address?: string | null;
  latitude?: number;
  longitude?: number;
  placeId?: string | null;
  isDefault?: boolean;
  active?: boolean;
  completedFormCount?: number;
}

interface LocationDraft {
  label: string;
  address: string;
  latitude: number | null;
  longitude: number | null;
  placeId: string;
  active: boolean;
}

const emptyDraft = (): LocationDraft => ({
  label: '',
  address: '',
  latitude: null,
  longitude: null,
  placeId: '',
  active: true,
});

const draftFromLocation = (location: CompanyLocation): LocationDraft => ({
  label: location.label || '',
  address: location.address || '',
  latitude: location.latitude ?? null,
  longitude: location.longitude ?? null,
  placeId: location.placeId || '',
  active: location.active !== false,
});

export const CompanyLocationsSection = () => {
  const notify = useNotify();
  const { data: locations = [], isLoading, refetch } = useGetList<CompanyLocation>(
    'locations',
    {
      pagination: { page: 1, perPage: 50 },
      sort: { field: 'id', order: 'ASC' },
      filter: { includeInactive: true },
    },
  );
  const [createLocation, { isLoading: creating }] = useCreate();
  const [updateLocation, { isLoading: updating }] = useUpdate();
  const [deleteLocation, { isLoading: deleting }] = useDelete();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<CompanyLocation | null>(null);
  const [draft, setDraft] = useState<LocationDraft>(emptyDraft());
  const [deleteTarget, setDeleteTarget] = useState<CompanyLocation | null>(null);
  const lastAutoLabelRef = useRef('');

  const saving = creating || updating;

  const openCreate = () => {
    setEditing(null);
    setDraft(emptyDraft());
    lastAutoLabelRef.current = '';
    setDialogOpen(true);
  };

  const openEdit = (location: CompanyLocation) => {
    setEditing(location);
    setDraft(draftFromLocation(location));
    lastAutoLabelRef.current = location.address || '';
    setDialogOpen(true);
  };

  const handlePlaceSelected = (place: SelectedPlace) => {
    setDraft((prev) => {
      const nextLabel =
        !prev.label || prev.label === lastAutoLabelRef.current
          ? place.address
          : prev.label;
      lastAutoLabelRef.current = place.address;
      return {
        ...prev,
        address: place.address,
        latitude: place.latitude,
        longitude: place.longitude,
        placeId: place.placeId || '',
        label: nextLabel,
      };
    });
  };

  const handleSave = () => {
    if (!draft.label.trim()) {
      notify('Enter a label for this location', { type: 'warning' });
      return;
    }
    if (draft.latitude == null || draft.longitude == null) {
      notify('Search for an address to set this location', { type: 'warning' });
      return;
    }

    const payload = {
      label: draft.label.trim(),
      address: draft.address,
      latitude: draft.latitude,
      longitude: draft.longitude,
      placeId: draft.placeId || undefined,
      active: draft.active,
    };

    const onSuccess = () => {
      notify(editing ? 'Location updated' : 'Location added', { type: 'success' });
      setDialogOpen(false);
      refetch();
    };
    const onError = () => {
      notify(editing ? 'Failed to update location' : 'Failed to add location', { type: 'error' });
    };

    if (editing) {
      updateLocation(
        'locations',
        { id: editing.id, data: payload, previousData: editing },
        { onSuccess, onError },
      );
    } else {
      createLocation('locations', { data: payload }, { onSuccess, onError });
    }
  };

  const patchLocation = (location: CompanyLocation, data: Partial<CompanyLocation>, successMessage: string) => {
    updateLocation(
      'locations',
      { id: location.id, data, previousData: location },
      {
        onSuccess: () => {
          notify(successMessage, { type: 'success' });
          refetch();
        },
        onError: () => notify('Failed to update location', { type: 'error' }),
      },
    );
  };

  const handleDelete = () => {
    if (!deleteTarget) return;
    deleteLocation(
      'locations',
      { id: deleteTarget.id, previousData: deleteTarget },
      {
        onSuccess: () => {
          notify('Location deleted', { type: 'success' });
          setDeleteTarget(null);
          refetch();
        },
        onError: () => notify('This location cannot be deleted. Deactivate it instead.', { type: 'error' }),
      },
    );
  };

  return (
    <>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Yards, warehouses, and other sites used when completing forms.
      </Typography>

      {isLoading ? (
        <Typography variant="body2" color="text.secondary">Loading locations…</Typography>
      ) : locations.length === 0 ? (
        <Typography variant="body2" sx={{ mb: 2 }}>
          No locations yet. Add a yard or warehouse to use on completed forms.
        </Typography>
      ) : (
        <Box sx={{ mb: 2 }}>
          {locations.map((location) => (
            <Box
              key={location.id}
              sx={{
                py: 1.5,
                borderBottom: '1px solid',
                borderColor: 'divider',
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                gap: 2,
                flexWrap: 'wrap',
              }}
            >
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Typography variant="body1">{location.label}</Typography>
                  {location.isDefault && <Chip label="Default" color="primary" size="small" />}
                  {location.active === false && <Chip label="Inactive" size="small" />}
                </Box>
                {location.address && (
                  <Typography variant="body2" color="text.secondary">
                    {location.address}
                  </Typography>
                )}
              </Box>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {!location.isDefault && location.active !== false && (
                  <Button size="small" onClick={() => patchLocation(location, { isDefault: true }, 'Default location updated')}>
                    Set as default
                  </Button>
                )}
                <Button
                  size="small"
                  onClick={() =>
                    patchLocation(
                      location,
                      { active: location.active === false },
                      location.active === false ? 'Location activated' : 'Location deactivated',
                    )
                  }
                >
                  {location.active === false ? 'Activate' : 'Deactivate'}
                </Button>
                <Button size="small" onClick={() => openEdit(location)}>
                  Edit
                </Button>
                {(location.completedFormCount ?? 0) === 0 && (
                  <Button size="small" color="error" onClick={() => setDeleteTarget(location)}>
                    Delete
                  </Button>
                )}
              </Box>
            </Box>
          ))}
        </Box>
      )}

      <Button variant="outlined" startIcon={<AddIcon />} onClick={openCreate}>
        Add location
      </Button>

      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        fullWidth
        maxWidth="sm"
        disableEnforceFocus
      >
        <DialogTitle>{editing ? 'Edit location' : 'Add location'}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Search for an address to set this location&apos;s coordinates. The label defaults to the address but can be edited.
          </Typography>
          <LocationAutocompleteInput
            key={editing ? `edit-${editing.id}` : 'create'}
            onPlaceSelected={handlePlaceSelected}
          />
          {draft.address && (
            <Typography variant="body2" color="text.secondary">
              Selected address: {draft.address}
            </Typography>
          )}
          <TextField
            label="Label"
            value={draft.label}
            onChange={(e) => setDraft((prev) => ({ ...prev, label: e.target.value }))}
            fullWidth
            required
          />
          <FormControlLabel
            control={
              <Switch
                checked={draft.active}
                onChange={(e) => setDraft((prev) => ({ ...prev, active: e.target.checked }))}
              />
            }
            label="Active"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Delete location?</DialogTitle>
        <DialogContent>
          <Typography>
            {deleteTarget?.label} has no form submissions and will be permanently removed.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDelete} disabled={deleting}>
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
